import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { REVIEWS } from "../../data/content";
import { Reveal } from "./Reveal";

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={`${n} star rating`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} className={i < n ? "fill-gold text-gold" : "text-white/20"} />
    ))}
  </div>
);

const initials = (name) => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export const Testimonials = () => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", dragFree: false });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => embla && setSelected(embla.selectedScrollSnap()), [embla]);
  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
    const id = setInterval(() => embla.scrollNext(), 5000);
    return () => { embla.off("select", onSelect); clearInterval(id); };
  }, [embla, onSelect]);

  return (
    <section id="reviews" className="relative border-y border-white/8 bg-surface py-24 sm:py-32 scroll-mt-20 grain overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <Reveal>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
                <SiGoogle /> Google Reviews
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-3 font-display text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-cream">
                Loved by <span className="text-gold-gloss">Winnipeg</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-4 rounded-2xl border border-gold/20 glass px-5 py-4">
              <span className="font-display text-4xl font-semibold text-gold">4.9</span>
              <div>
                <Stars n={5} />
                <p className="mt-1 text-xs text-cream/55">Rated on Google</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-12">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {REVIEWS.map((r, i) => (
                <div key={i} className="min-w-0 shrink-0 grow-0 basis-[88%] pr-4 sm:basis-1/2 lg:basis-1/3">
                  <div className="flex h-full flex-col rounded-3xl border border-white/8 bg-ink p-6 sm:p-7" data-testid={`review-${i}`}>
                    <div className="flex items-center justify-between">
                      <Stars n={r.rating} />
                      <Quote className="text-gold/30" size={26} />
                    </div>
                    <p className="mt-4 flex-1 text-sm sm:text-base text-cream/75 leading-relaxed">"{r.text}"</p>
                    <div className="mt-6 flex items-center gap-3 border-t border-white/8 pt-5">
                      <span className="grid h-11 w-11 place-items-center rounded-full bg-gold/15 border border-gold/30 font-display text-sm font-semibold text-gold">
                        {initials(r.name)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-cream">{r.name}</p>
                        <p className="truncate text-xs text-cream/45">{r.tag}</p>
                      </div>
                      <SiGoogle className="ml-auto text-cream/40" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button onClick={() => embla && embla.scrollPrev()} aria-label="Previous reviews" data-testid="reviews-prev" className="h-11 w-11 grid place-items-center rounded-full border border-white/12 text-cream hover:border-gold/40 hover:text-gold transition-colors">
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => embla && embla.scrollNext()} aria-label="Next reviews" data-testid="reviews-next" className="h-11 w-11 grid place-items-center rounded-full border border-white/12 text-cream hover:border-gold/40 hover:text-gold transition-colors">
              <ChevronRight size={20} />
            </button>
            <div className="ml-2 flex gap-1.5">
              {REVIEWS.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === selected ? "w-6 bg-gold" : "w-1.5 bg-white/20"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
